import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MailCheck } from "lucide-react";
import api from "../api/http";
import type { MessageResponse } from "../api/types";
import { AuthLayout } from "../components/auth/AuthLayout";
import {
  authInput,
  authLabel,
  authLink,
  authMuted,
  authPrimaryBtn,
} from "../components/auth/authStyles";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post<MessageResponse>("/api/v1/auth/password-reset/", { email });
    } catch {
      // Silently succeed — backend always returns 200 to prevent enumeration
    }
    setSubmitted(true);
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 ring-1 ring-blue-200 text-blue-600 dark:bg-blue-500/15 dark:ring-blue-500/30 dark:text-blue-300">
            <MailCheck size={22} />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{t("auth.forgot.successTitle")}</h2>
          <p className={`mt-2 text-sm ${authMuted}`}>
            {t("auth.forgot.successBody", { email })}
          </p>
          <p className={`mt-1 text-xs ${authMuted}`}>{t("auth.forgot.successExpiry")}</p>
          <Link to="/login" className={`mt-6 inline-block text-sm ${authLink}`}>
            {t("auth.forgot.backLink")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.forgot.subtitle")}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className={authLabel}>
            {t("auth.forgot.email")}
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInput}
            placeholder={t("auth.forgot.emailPlaceholder")}
          />
        </div>

        <button type="submit" disabled={isSubmitting} className={authPrimaryBtn}>
          {isSubmitting ? t("auth.forgot.submitting") : t("auth.forgot.submit")}
        </button>
      </form>

      <div className={`mt-5 text-center text-sm ${authMuted}`}>
        <Link to="/login" className={authLink}>
          {t("auth.forgot.backLink")}
        </Link>
      </div>

      <div className={`mt-5 border-t border-slate-200 pt-4 text-center text-sm dark:border-white/5 ${authMuted}`}>
        {t("auth.login.signupPrompt")}{" "}
        <Link to="/register" className={authLink}>
          {t("auth.login.signupLink")}
        </Link>
      </div>
    </AuthLayout>
  );
}
